/**
 * PageIndicatorElement - Complex editor element for page indicator with type selection
 *
 * Features:
 * - Type selector (Line/Dashes/DashesWithTitle)
 * - Conditional rendering based on selected type:
 *   - Line: slider + color/trackColor styles
 *   - Dashes: numberOfDashes, dashHeight, dashSpacing + filledColor/notFilledColor
 * - Universal fields (available for all kinds):
 *   - Title: optional TextElement for progress title
 *   - Image/Logo and Progress Layout: injected via children prop
 *
 * Sprint: screen-editor-header
 * Task: Page Indicator Types
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Typography,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  Slider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { StyledNumberInput, WithGuideHelp } from '@modules/ui-components';
import type { FieldConfig } from '../../types/editorFieldTypes';
import { useEditorGuideService } from '../../contexts';
import {
  fieldInputSx,
  fieldSelectSx,
  fieldMenuItemSx,
  fieldLabelSx,
  mainFieldLabelEnabledSx,
  mainFieldLabelDisabledSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  SHOW_FIELD_PATH_TOOLTIP,
  expandedSectionSx,
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../config/editorFieldStyles';
import {
  FIELD_MARGIN_BOTTOM,
  FIELD_GAP,
  FIELD_GAP_SMALL,
  FIELD_STYLE_GRID_GAP,
  FIELD_STYLE_MARGIN_TOP,
} from '../../config/ui';
import { TextElement } from './TextElement';
import { StyleFieldRow } from '../../components/shared/styleFields';
import { getStyleInputTestId } from '../../utils/testIdUtils';
import type { LanguageCode } from '@segmently/schema-models/v2';

/** Page Indicator Kind values */
export type PageIndicatorKindType = 'Line' | 'Dashes' | 'DashesWithTitle';

/** Width of the number input in pixels */
const NUMBER_INPUT_WIDTH = 70;

/**
 * Props for PageIndicatorElement component
 */
export interface PageIndicatorElementProps {
  /** Field configuration */
  fieldConfig: FieldConfig;

  /** Current indicator kind (Line/Dashes/DashesWithTitle) */
  indicatorKind: PageIndicatorKindType;

  /** Line indicator value (0-100) */
  lineValue: number;

  /** Whether field is enabled (checkbox state) */
  enabled: boolean;

  /** Callback when indicator kind changes */
  onKindChange: (kind: PageIndicatorKindType) => void;

  /** Callback when line value changes */
  onLineValueChange: (value: number) => void;

  /** Callback when enabled state changes */
  onEnabledChange: (enabled: boolean) => void;

  /** Whether entire element is disabled (save state) */
  disabled?: boolean;

  /** Whether style settings are expanded */
  expandedStyles?: boolean;

  /** Callback when style expansion toggles */
  onStylesExpandToggle?: () => void;

  /** Style values for Line type */
  lineStyleValues?: Record<string, unknown>;

  /** Callback when Line style value changes */
  onLineStyleChange?: (path: string, value: unknown) => void;

  // Dashes-specific props
  /** Number of dashes */
  dashesNumber?: number;
  /** Dash height */
  dashHeight?: number;
  /** Dash spacing */
  dashSpacing?: number;

  /** Callback when dashes config changes */
  onDashesConfigChange?: (field: string, value: number) => void;

  /** Style values for Dashes type */
  dashesStyleValues?: Record<string, unknown>;

  /** Callback when Dashes style value changes */
  onDashesStyleChange?: (path: string, value: unknown) => void;

  // Title-specific props (for DashesWithTitle)
  /** Title text value */
  titleValue?: string;
  /** Whether title is enabled */
  titleEnabled?: boolean;
  /** Title style values */
  titleStyleValues?: Record<string, unknown>;
  /** Callback when title value changes */
  onTitleChange?: (value: string) => void;
  /** Callback when title enabled changes */
  onTitleEnabledChange?: (enabled: boolean) => void;
  /** Callback when title style changes */
  onTitleStyleChange?: (path: string, value: unknown) => void;
  /** Whether title styles are expanded */
  titleStylesExpanded?: boolean;
  /** Callback when title styles expand toggles */
  onTitleStylesExpandToggle?: () => void;

  /** Current locale for l10n */
  locale?: string;

  /** data-testid for the main field container */
  'data-testid'?: string;

  /** data-testid for the checkbox */
  checkboxTestId?: string;

  /** data-testid for the styles toggle button (gear icon) */
  stylesToggleTestId?: string;

  /** data-testid for the input field */
  inputTestId?: string;

  /** data-testid for the slider element */
  sliderTestId?: string;

  /** data-testid for the indicator-kind Select (caller-supplied so the shared element stays context-agnostic) */
  kindSelectTestId?: string;

  /** Additional sub-sections rendered inside the gear Collapse */
  children?: React.ReactNode;
}

/** Options for indicator kind selector */
const INDICATOR_KIND_OPTIONS: Array<{ value: PageIndicatorKindType; label: string }> = [
  { value: 'Line', label: 'Line' },
  { value: 'Dashes', label: 'Dashes' },
  { value: 'DashesWithTitle', label: 'DashesWithTitle' },
];

/**
 * PageIndicatorElement - Complex editor element for page indicator
 */
export const PageIndicatorElement: React.FC<PageIndicatorElementProps> = ({
  fieldConfig,
  indicatorKind,
  lineValue,
  enabled,
  onKindChange,
  onLineValueChange,
  onEnabledChange,
  disabled = false,
  expandedStyles: expandedStylesProp,
  onStylesExpandToggle,
  lineStyleValues = {},
  onLineStyleChange,
  dashesNumber = 5,
  dashHeight = 5,
  dashSpacing = 7,
  onDashesConfigChange,
  dashesStyleValues = {},
  onDashesStyleChange,
  titleValue = '',
  titleEnabled = true,
  titleStyleValues = {},
  onTitleChange,
  onTitleEnabledChange,
  onTitleStyleChange,
  titleStylesExpanded,
  onTitleStylesExpandToggle,
  locale = 'en-US' as LanguageCode,
  'data-testid': dataTestId,
  checkboxTestId,
  stylesToggleTestId,
  inputTestId,
  sliderTestId,
  kindSelectTestId,
  children,
}) => {
  // Internal state for style expansion
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isStylesExpanded = expandedStylesProp ?? internalExpanded;

  const hasStyleFields = fieldConfig.styleFields && fieldConfig.styleFields.length > 0;
  const hasDashesStyleFields = fieldConfig.dashesStyleFields && fieldConfig.dashesStyleFields.length > 0;

  // Get guideService from context for WithGuideHelp
  const guideService = useEditorGuideService();

  // Get min/max/step from config
  const min = fieldConfig.min ?? 0;
  const max = fieldConfig.max ?? 100;
  const step = fieldConfig.step ?? 1;

  // Handle checkbox toggle
  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      onEnabledChange(event.target.checked);
    },
    [disabled, onEnabledChange]
  );

  // Handle kind selector change
  const handleKindChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }> | { target: { value: unknown } }) => {
      onKindChange(event.target.value as PageIndicatorKindType);
    },
    [onKindChange]
  );

  // Handle slider change (Line type)
  const handleSliderChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      if (typeof newValue === 'number') {
        onLineValueChange(newValue);
      }
    },
    [onLineValueChange]
  );

  // Handle number input change (Line type)
  const handleNumberChange = useCallback(
    (newValue: number | null) => {
      // null (cleared input) coerces to 0 in the clamp, as before
      const clampedValue = Math.max(min, Math.min(max, newValue ?? 0));
      onLineValueChange(clampedValue);
    },
    [onLineValueChange, min, max]
  );

  // Handle gear icon click
  const handleGearClick = useCallback(() => {
    if (onStylesExpandToggle) {
      onStylesExpandToggle();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  }, [onStylesExpandToggle]);

  // Handle dashes config change
  const handleDashesConfigChange = useCallback(
    (field: string, value: number) => {
      if (onDashesConfigChange) {
        onDashesConfigChange(field, value);
      }
    },
    [onDashesConfigChange]
  );

  const showDashesFields = indicatorKind === 'Dashes' || indicatorKind === 'DashesWithTitle';

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
        mb: FIELD_MARGIN_BOTTOM,
      }}
    >
      {/* Label Row: Checkbox + Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL }}>
        <Checkbox
          data-testid={checkboxTestId}
          checked={enabled}
          onChange={handleCheckboxChange}
          disabled={disabled}
          size="small"
          icon={<Box sx={checkboxIconSx} />}
          checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          sx={checkboxSx}
          inputProps={{
            'aria-label': fieldConfig.label,
          }}
        />
        {/* Label with optional guide help */}
        {fieldConfig.guideKey && guideService ? (
          <WithGuideHelp
            guideKey={fieldConfig.guideKey}
            guideService={guideService}
            data-testid={`guide-anchor-${fieldConfig.guideKey}`}
            position="inline-end"
            iconSize="small"
          >
            <Tooltip
              title={SHOW_FIELD_PATH_TOOLTIP ? fieldConfig.path : ''}
              placement="top"
              arrow
              disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
            >
              <Typography
                variant="body2"
                sx={enabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}
                data-testid={fieldConfig.path}
                aria-label={fieldConfig.path}
              >
                {fieldConfig.label}
              </Typography>
            </Tooltip>
          </WithGuideHelp>
        ) : (
          <Tooltip
            title={SHOW_FIELD_PATH_TOOLTIP ? fieldConfig.path : ''}
            placement="top"
            arrow
            disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
          >
            <Typography
              variant="body2"
              sx={enabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}
              data-testid={fieldConfig.path}
              aria-label={fieldConfig.path}
            >
              {fieldConfig.label}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Input Row: NumberInput + Slider + Gear (как в NumberSliderElement) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP }}>
        <StyledNumberInput
          value={enabled ? lineValue : 0}
          onChange={handleNumberChange}
          disabled={disabled || !enabled}
          size="small"
          min={min}
          max={max}
          step={step}
          data-testid={inputTestId}
          sx={{
            width: NUMBER_INPUT_WIDTH,
            minWidth: NUMBER_INPUT_WIDTH,
          }}
        />
        <Slider
          value={enabled ? lineValue : 0}
          onChange={handleSliderChange}
          disabled={disabled || !enabled}
          min={min}
          max={max}
          step={step}
          aria-label={fieldConfig.label}
          slotProps={{
            input: {
              'data-testid': sliderTestId,
            } as React.InputHTMLAttributes<HTMLInputElement>,
          }}
          sx={{
            flex: 1,
            '& .MuiSlider-thumb': { width: 14, height: 14 },
            '& .MuiSlider-track': { height: 4 },
            '& .MuiSlider-rail': { height: 4 },
          }}
        />
        <IconButton
          data-testid={stylesToggleTestId}
          onClick={handleGearClick}
          size="small"
          disabled={disabled}
          aria-label="Toggle style settings"
          sx={isStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
        >
          <TuneIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ВСЕ настройки внутри Collapse */}
      <Collapse in={isStylesExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: FIELD_STYLE_MARGIN_TOP, ...expandedSectionSx }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: FIELD_STYLE_GRID_GAP,
            }}
          >
          {/* Indicator Kind - всегда первое поле */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: FIELD_GAP_SMALL }}>
            <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>
              indicator kind
            </Typography>
            <FormControl size="small" fullWidth disabled={disabled || !enabled} variant="standard">
              <Select
                value={indicatorKind}
                onChange={handleKindChange as any}
                displayEmpty
                sx={fieldSelectSx}
                data-testid={kindSelectTestId}
              >
                {INDICATOR_KIND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={fieldMenuItemSx}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* ========== LINE TYPE SETTINGS ========== */}
          {indicatorKind === 'Line' && hasStyleFields && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: FIELD_STYLE_GRID_GAP,
              }}
            >
              {fieldConfig.styleFields!.map((styleConfig) => (
                <StyleFieldRow
                  key={styleConfig.path}
                  config={styleConfig}
                  value={lineStyleValues[styleConfig.path]}
                  onChange={(newValue) => onLineStyleChange?.(styleConfig.path, newValue)}
                  disabled={disabled || !enabled}
                  inputTestId={getStyleInputTestId(fieldConfig.label, styleConfig.label)}
                />
              ))}
            </Box>
          )}

          {/* ========== DASHES TYPE SETTINGS ========== */}
          {showDashesFields && (
            <>
              {/* Dashes config: number, height, spacing */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                }}
              >
                {/* Dashes Number */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: FIELD_GAP_SMALL }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={fieldLabelSx}
                    data-testid="navigationBar.dashesPageIndicator.numberOfDashes"
                    aria-label="navigationBar.dashesPageIndicator.numberOfDashes"
                  >
                    dashes number
                  </Typography>
                  <StyledNumberInput
                    value={dashesNumber}
                    onChange={(val: number | null) => handleDashesConfigChange('numberOfDashes', val as number)}
                    disabled={disabled || !enabled}
                    size="small"
                    variant="standard"
                    min={1}
                    max={20}
                    step={1}
                    data-testid="input-dashes-number"
                    sx={fieldInputSx}
                  />
                </Box>

                {/* Dash Height */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: FIELD_GAP_SMALL }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={fieldLabelSx}
                    data-testid="navigationBar.dashesPageIndicator.dashHeight"
                    aria-label="navigationBar.dashesPageIndicator.dashHeight"
                  >
                    dash height
                  </Typography>
                  <StyledNumberInput
                    value={dashHeight}
                    onChange={(val: number | null) => handleDashesConfigChange('dashHeight', val as number)}
                    disabled={disabled || !enabled}
                    size="small"
                    variant="standard"
                    min={1}
                    max={20}
                    step={1}
                    data-testid="input-dash-height"
                    sx={fieldInputSx}
                  />
                </Box>

                {/* Dash Spacing */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: FIELD_GAP_SMALL }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={fieldLabelSx}
                    data-testid="navigationBar.dashesPageIndicator.dashesSpacing"
                    aria-label="navigationBar.dashesPageIndicator.dashesSpacing"
                  >
                    dash spacing
                  </Typography>
                  <StyledNumberInput
                    value={dashSpacing}
                    onChange={(val: number | null) => handleDashesConfigChange('dashesSpacing', val as number)}
                    disabled={disabled || !enabled}
                    size="small"
                    variant="standard"
                    min={0}
                    max={20}
                    step={1}
                    data-testid="input-dash-spacing"
                    sx={fieldInputSx}
                  />
                </Box>
              </Box>

              {/* Dashes Styles */}
              {hasDashesStyleFields && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: FIELD_STYLE_GRID_GAP,
                  }}
                >
                  {fieldConfig.dashesStyleFields!.map((styleConfig) => (
                    <StyleFieldRow
                      key={styleConfig.path}
                      config={styleConfig}
                      value={dashesStyleValues[styleConfig.path]}
                      onChange={(newValue) => onDashesStyleChange?.(styleConfig.path, newValue)}
                      disabled={disabled || !enabled}
                      inputTestId={getStyleInputTestId(fieldConfig.label, styleConfig.label)}
                    />
                  ))}
                </Box>
              )}

            </>
          )}

          {/* Title Element (universal — available for any indicator kind) */}
          {fieldConfig.titleConfig && (
            <Box sx={{ mt: 1 }}>
              <TextElement
                fieldConfig={fieldConfig.titleConfig}
                value={titleValue}
                templateValue={undefined}
                enabled={titleEnabled}
                locale={locale}
                onChange={onTitleChange || (() => {})}
                onEnabledChange={onTitleEnabledChange || (() => {})}
                disabled={disabled || !enabled}
                expandedStyles={titleStylesExpanded}
                onStylesExpandToggle={onTitleStylesExpandToggle}
                styleValues={titleStyleValues}
                onStyleChange={onTitleStyleChange}
                data-testid="field-page-indicator-title"
                checkboxTestId="checkbox-page-indicator-title"
                stylesToggleTestId="styles-toggle-page-indicator-title"
                styleGroupTestId="styles-group-page-indicator-title"
                inputTestId="input-page-indicator-title"
              />
            </Box>
          )}

          {/* Additional sub-sections injected by consumer */}
          {children}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PageIndicatorElement;
